/* eslint-disable no-param-reassign */
import {
  ActionReducerMapBuilder,
  createAsyncThunk,
  createSlice,
  PayloadAction,
} from '@reduxjs/toolkit';

import { filterType } from '../../config/enum';
import DefaultApiParameters from '../../enum/DefaultApiParameters';
import {
  APIConsumerGuestChargingResponseInterface,
  ConsumerGuestChargingInitialProperties,
  RequestConsumerGuestChargingInterFace,
} from '../../interface/end-consumer-guest-charging-interface';
import { CommonFilterProperties, FilterUpdateProperties } from '../../interface/filter.interface';
import {
  ClearFilters,
  ConfigureListFilterResponse,
  RemoveSingleFilterFromList,
  UpdateCommonFilter,
} from '../../shared-functions/CommonFilters';
import { removeEmptyKeys } from '../../shared-functions/DateTime';
import endConsumerApiV2 from '../../utils/api/end-consumer-v2';

import { setNotificationMessage } from './NotificationManagementSlice';

export const fetchConsumerGuestCharging = createAsyncThunk<
  APIConsumerGuestChargingResponseInterface,
  RequestConsumerGuestChargingInterFace
>(
  'end-consumers/fetchConsumerGuestCharging',
  async (data: RequestConsumerGuestChargingInterFace, { dispatch }) => {
    const { userCustomId, ...query } = data;
    if (!userCustomId) {
      throw new Error('userCustomId is required');
    }

    try {
      const response = await endConsumerApiV2.fetchGuestFreeChargingList(
        userCustomId,
        removeEmptyKeys(query),
      );
      return response.data;
    } catch (error: any) {
      dispatch(
        setNotificationMessage({
          type: 'error',
          message: error?.response?.data?.message,
          code: error?.response?.data?.code,
          status: true,
        }),
      );
      throw error;
    }
  },
);

export const fetchConsumerGuestChargingFilter = createAsyncThunk<CommonFilterProperties[], void>(
  'end-consumers/fetchConsumerGuestChargingFilter',
  async (_, { dispatch, getState }) => {
    const state: any = getState();
    const { selectedEndConsumer } = state.endConsumerType;

    if (!selectedEndConsumer?.userCustomId) {
      throw new Error('CustomId is required');
    }
    try {
      const response = await endConsumerApiV2.fetchGuestFreeChargingFilters(
        selectedEndConsumer.userCustomId,
      );
      const updatedResponse: CommonFilterProperties[] = ConfigureListFilterResponse(response.data);
      return updatedResponse;
    } catch (error: any) {
      dispatch({
        type: 'notification/setNotificationMessage',
        payload: {
          type: 'error',
          message: error?.response?.data?.message || 200,
          code: error?.response?.data?.code || 'Something went wrong!',
          status: true,
        },
      });
      throw error;
    }
  },
);

const availableFilters: CommonFilterProperties[] = [
  {
    name: 'Search here...',
    key: 'searchBy',
    type: filterType.TEXT,
    value: '',
    applyFilter: false,
    enable: true,
    searchBy: true,
  },
];

// Initial state
const initialState: ConsumerGuestChargingInitialProperties = {
  consumers: [],
  limit: DefaultApiParameters.limit,
  page: DefaultApiParameters.page,
  totalPages: 0,
  totalResults: 0,
  loading: false,
  filter: {},
  filters: availableFilters,
  filterListLoading: false,
  isFilterUpdated: false,
  sortBy: '',
  sortField: '',
};

export const ConsumerGuestCharging = createSlice({
  name: 'end-consumers-Guest-commerical',
  initialState,
  reducers: {
    /**
     * Update By month filters
     * @param state
     * @param action
     */
    updateConsumerGuestChargingListFilter: (
      state,
      action: PayloadAction<FilterUpdateProperties>,
    ) => {
      const payload = { ...action.payload };
      if (payload.type === 'add') {
        const { updatedCommonFilter, isFilterUpdated } = UpdateCommonFilter(
          state.filters,
          action.payload,
        );
        state.filters = updatedCommonFilter;
        state.isFilterUpdated = isFilterUpdated;
      } else if (payload.type === 'remove') {
        state.filters = RemoveSingleFilterFromList(state.filters, payload.key);
        state.isFilterUpdated = true;
      } else if (payload.type === 'clear') {
        state.filters = ClearFilters(state.filters);
        state.isFilterUpdated = true;
      }
    },
    /**
     * function for set sortBy
     * @param state
     * @param action
     */
    setSortData: (state, action: PayloadAction<{ sortField: string; sortBy: 'asc' | 'desc' }>) => {
      state.sortField = action.payload.sortField;
      state.sortBy = action.payload.sortBy;
    },
  },
  /**
   * Use the extra reducers function
   * @param builder
   */
  extraReducers: (builder: ActionReducerMapBuilder<ConsumerGuestChargingInitialProperties>) => {
    builder
      .addCase(
        fetchConsumerGuestCharging.pending,
        (state: ConsumerGuestChargingInitialProperties) => {
          state.loading = true;
        },
      )
      .addCase(
        fetchConsumerGuestCharging.fulfilled,
        (
          state: ConsumerGuestChargingInitialProperties,
          action: PayloadAction<APIConsumerGuestChargingResponseInterface>,
        ) => {
          state.loading = false;
          state.consumers = [...action.payload.results];
          state.limit = action.payload.limit;
          state.page = action.payload.page;
          state.totalPages = action.payload.totalPages;
          state.totalResults = action.payload.totalResults;
        },
      )
      .addCase(
        fetchConsumerGuestCharging.rejected,
        (state: ConsumerGuestChargingInitialProperties) => {
          state.loading = false;
          // Handle error if needed
        },
      );

    builder
      .addCase(fetchConsumerGuestChargingFilter.pending, (state) => {
        state.filterListLoading = true;
      })
      .addCase(
        fetchConsumerGuestChargingFilter.fulfilled,
        (state, action: PayloadAction<CommonFilterProperties[]>) => {
          state.filterListLoading = false;
          state.filters = [...(state.filters || []), ...action.payload];
        },
      )
      .addCase(fetchConsumerGuestChargingFilter.rejected, (state) => {
        state.filterListLoading = false;
      });
  },
});

// Export actions
export const { updateConsumerGuestChargingListFilter, setSortData } = ConsumerGuestCharging.actions;
// Export the reducer
export default ConsumerGuestCharging.reducer;
